import { IApiSymbol } from './IApiSymbol';
import { IApiStreamBase } from './IApiStream';
import { IApiScheduleFullView } from './IApiSchedule';
import { ApiServerType } from './IApiServerType';
import { IdAndNameOf } from './IApiBaseResource';
import { IApiNode } from './IApiNode';
import { IApiMarkup, ApiMarkupType } from './IApiMarkup';
import { IApiMarkupProfile } from './IApiMarkupProfile';
import { IApiClient } from './IApiClient';
import { IApiLpSymbol } from './IApiLpSymbol';
import { ApiPricingMode, ApiAggregationMethod } from './IApiPricingProfile';
import { IApiAdjustmentProfileData } from './IApiAdjustmentProfile';
import { IApiSpread } from './IApiSpreadProfile';
import { IApiProfileBase } from './IApiProfileBase';

export enum ApiClientSymbolFilter {
  NonTradable = 'nonTradable', //Only for Get Server Client Symbols
  Tradable = 'tradable', //Only for Get Server Client Symbols
}

export enum ApiClientSymbolInclude {
  Pricing = 'pricing',
  Routing = 'routing',
}

export interface IApiClientCoreSymbol {
  id: IApiSymbol['id'];
  name: string;
  symbolGroup: string;
  baseAsset?: string;
  quoteAsset: string;
  /**
   * in milliseconds
   */
  throttlingInterval?: number;
}

export interface IApiClientSymbol {
  coreSymbol: IApiClientCoreSymbol;
  schedule?: IdAndNameOf<IApiScheduleFullView>;
  name: string;
  stream: IApiStreamBase;
}

export interface IApiClientSymbolTradable extends Omit<IApiClientSymbol, 'coreSymbol'> {
  coreSymbol: IApiSymbol;
  isTradable?: boolean;
  /**
   * ThrottlingInterval per server, in milliseconds
   */
  throttlingIntervalServer?: number;
  /**
   * ThrottlingInterval per core symbol, in milliseconds
   */
  throttlingIntervalCoreSymbol?: number;
}

export interface IApiServerClientSymbolWithExtraProps extends IApiClientSymbolTradable {
  adjustmentProfile: IApiAdjustmentProfileData;
  lpMultipliers?: {
    price?: number;
    volume?: number;
  };
}

export interface IApiServerClientSymbolWithPricingProps extends IApiServerClientSymbolWithExtraProps {
  markup?: IApiClientSymbolMarkup;
  spread?: {
    tobMinSpread: number | null;
    tobMaxSpread: number | null;
  };
  priceFilteringProfile: IApiClientSymbolPriceFilteringProfile;
  lpMarkups?: {
    bid?: number;
    ask?: number;
    type?: ApiMarkupType;
  };
}

export enum ApiExecutionType {
  BookA = 'BOOK_A',
  BookB = 'BOOK_B',
  Reject = 'REJECT',
}

export interface IApiServerClientSymbolWithRoutingProps extends IApiServerClientSymbolWithExtraProps {
  executionType: ApiExecutionType;
  pricingMode?: ApiPricingMode;
  lpVolumeValidation?: {
    min: number;
    max: number;
    step: number;
  };
}

export type IApiServerClientSymbol = IApiClientSymbolTradable
  | IApiServerClientSymbolWithPricingProps
  | IApiServerClientSymbolWithRoutingProps
  | (IApiServerClientSymbolWithPricingProps & IApiServerClientSymbolWithRoutingProps);

export interface IApiClientCoreSymbolDetails {
  id: number;
  symbolGroup: string;
  name: string;
  baseAsset?: string;
  priceDigits: number;
  quoteAsset: string;
  volumeDigits: number;
  pricingTimeout?: number;
  /**
   * in milliseconds
   */
  throttlingInterval?: number;
}

interface IApiClientSymbolClientDetails extends Omit<IApiClient, 'name' | 'type'> {
}

export interface IApiClientSymbolMarkup extends Omit<IApiMarkup, 'id' | 'name'> {
  profile?: IApiMarkupProfile;
}

export interface IApiClientSymbolLp {
  id: number;
  name: string;
  lpSymbol?: Omit<IApiLpSymbol, 'coreSymbolId' | 'coreSymbolName'>;
}

export interface IApiClientSymbolServer {
  id: string;
  name: string;
  type: ApiServerType;
  pricingClient?: IApiClientSymbolClientDetails;
  tradingClient?: IApiClientSymbolClientDetails;
  /**
   * in milliseconds
   */
  throttlingInterval?: number;
}

export interface IApiClientSymbolOrderLifetimeProfile {
  id?: number;
  name?: string;
  limitFokTimeout: number;
  limitIocTimeout: number;
  limitGtcTimeout: number;
  marketFokTimeout: number;
  marketIocTimeout: number;
  marketGtcTimeout: number;
}

export interface IApiClientSymbolAdjustmentProfile {
  id?: number;
  name?: string;
  priceMultiplier: number;
  volumeMultiplier: number;
}

export interface IApiClientSymbolPriceFilteringProfile {
  id?: number;
  name?: string;
  maxClientMarketDepth: number;
  maxBookbMarketDepth: number;
}

export interface IApiClientSymbolPricingProfile {
  id?: number;
  name?: string;
  mode: ApiPricingMode;
  aggregationMethod?: ApiAggregationMethod;
}

export interface IApiClientSymbolSpread {
  profile: IApiProfileBase;
  spreads: IApiSpread[];
  applyMaxToLower: boolean;
}

export interface IApiNodeClientSymbolDetails {
  name: string;
  coreSymbol: IApiClientCoreSymbolDetails;
  stream: IApiStreamBase;
  node: IdAndNameOf<IApiNode>;
  schedule?: IApiScheduleFullView;
  markup?: IApiClientSymbolMarkup;
  spread?: IApiClientSymbolSpread;
  servers: IApiClientSymbolServer[];
  lps: IApiClientSymbolLp[];
}

export interface IApiSymbolClientRoutingRule {
  accounts?: string[];
  accountRegexp?: string;
  accountGroups?: string[];
  accountGroupRegexp?: string;
  executionType?: ApiExecutionType;
}

export interface IApiServerClientSymbolDetails {
  name: string;
  coreSymbol: IApiClientCoreSymbolDetails;
  stream: IApiStreamBase;
  node: IdAndNameOf<IApiNode>;
  schedule?: IApiScheduleFullView;
  markup?: IApiClientSymbolMarkup;
  servers: IApiClientSymbolServer[];
  lps: IApiClientSymbolLp[];
  orderLifetimeProfile: IApiClientSymbolOrderLifetimeProfile;
  adjustmentProfile: IApiClientSymbolAdjustmentProfile;
  priceFilteringProfile: IApiClientSymbolPriceFilteringProfile;
  routingRules: IApiSymbolClientRoutingRule[];
  pricingProfile: IApiClientSymbolPricingProfile;
  spread?: IApiClientSymbolSpread;
}

export interface IApiClientSymbolCreate {
  coreSymbolId: IApiSymbol['id'];
  name: string;
  streamId: IApiStreamBase['id'];
}

export interface IApiClientSymbolIdentifier {
  nodeId: IApiNode['id'];
  clientSymbolName: IApiClientSymbol['name'];
}
